import React from 'react';
import { Info, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';
import Sparkline from './Sparkline';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

/**
 * Enhanced KpiTile - Brand KPI display with accessibility
 * Features:
 * - WCAG 2.1 AA compliant colors
 * - Micro-interactions on hover
 * - Responsive typography
 * - Screen reader support
 * - Info tooltips with plain language
 * - Backward compatibility with simple KpiTile props (status, subtitle, icon, trend)
 */

const accentColors = {
  primary: { bar: 'bg-urga-electricBlue', text: 'text-urga-electricBlue', bg: 'bg-blue-50', border: 'border-blue-200' },
  green: { bar: 'bg-semantic-success', text: 'text-semantic-success', bg: 'bg-green-50', border: 'border-green-200' },
  good: { bar: 'bg-semantic-success', text: 'text-semantic-success', bg: 'bg-green-50', border: 'border-green-200' },
  accent: { bar: 'bg-brand-accent', text: 'text-brand-accent', bg: 'bg-purple-50', border: 'border-purple-200' },
  bharat: { bar: 'bg-brand-bharat', text: 'text-brand-bharat', bg: 'bg-orange-50', border: 'border-orange-200' },
  link: { bar: 'bg-brand-link', text: 'text-brand-link', bg: 'bg-blue-50', border: 'border-blue-200' },
  purple: { bar: 'bg-brand-purple', text: 'text-brand-purple', bg: 'bg-purple-50', border: 'border-purple-200' },
  info: { bar: 'bg-brand-purple', text: 'text-brand-purple', bg: 'bg-purple-50', border: 'border-purple-200' },
  warning: { bar: 'bg-semantic-warning', text: 'text-semantic-warning', bg: 'bg-orange-50', border: 'border-orange-200' },
  amber: { bar: 'bg-semantic-warning', text: 'text-semantic-warning', bg: 'bg-orange-50', border: 'border-orange-200' },
  danger: { bar: 'bg-semantic-danger', text: 'text-semantic-danger', bg: 'bg-red-50', border: 'border-red-200' },
  red: { bar: 'bg-semantic-danger', text: 'text-semantic-danger', bg: 'bg-red-50', border: 'border-red-200' },
  error: { bar: 'bg-semantic-danger', text: 'text-semantic-danger', bg: 'bg-red-50', border: 'border-red-200' },
  grey: { bar: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' },
  gray: { bar: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' },
};

const KpiTile = ({ 
  title,
  value,
  unit = null,
  delta = null,
  subcopy = null,
  accentBar = 'primary',
  sparklineData = null,
  sparklineStroke = 'primary',
  sparklineColor = null,
  footer = null,
  definition = null,
  source = null,
  timestamp = null,
  dataType = null,
  info = null,
  priority = 'p1',
  className = '',
  onClick,
  interactive = false,
  // Backward compatibility props from simple KpiTile
  status = null,
  subtitle = null,
  icon = null,
  trend = null,
  dataTestId = null,
}) => {
  // Map backward compatibility props
  const effectiveAccentBar = status || accentBar;
  const effectiveSubcopy = subtitle || subcopy;
  const effectiveDelta = trend ? `${trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : ''} ${trend.value}` : delta;
  
  const accent = accentColors[effectiveAccentBar] || accentColors.primary;
  
  // Parse delta for styling
  const getDeltaInfo = () => {
    if (effectiveDelta === null || effectiveDelta === undefined) return null;
    const deltaStr = String(effectiveDelta);
    const numericDelta = parseFloat(deltaStr.replace(/[^-\d.]/g, ''));
    const isPositive = deltaStr.includes('↑') || numericDelta > 0;
    const isNegative = deltaStr.includes('↓') || numericDelta < 0;
    const isNeutral = !isPositive && !isNegative;
    
    return {
      value: effectiveDelta,
      isPositive,
      isNegative,
      isNeutral,
      icon: isPositive ? TrendingUp : isNegative ? TrendingDown : Minus,
      colorClass: isPositive ? 'text-semantic-success' : isNegative ? 'text-semantic-danger' : 'text-gray-500',
    };
  };
  
  const deltaInfo = getDeltaInfo();
  
  // Build footer text
  const footerParts = [];
  if (definition) footerParts.push(`Definition: ${definition}`);
  if (source) footerParts.push(`Source: ${source}`);
  if (dataType) footerParts.push(`Data Type: ${dataType}`);
  if (timestamp) footerParts.push(`Updated: ${timestamp}`);
  const finalFooter = footer || (footerParts.length > 0 ? footerParts.join(' • ') : null);
  
  // Priority-based sizing classes
  const priorityClasses = {
    p0: 'widget-p0',
    p1: 'widget-p1',
    p2: 'widget-p2',
  };

  return (
    <div
      role="region"
      aria-label={`${title}: ${value}${unit ? ` ${unit}` : ''}`}
      data-testid={dataTestId}
      className={cn(
        'kpi-tile group',
        interactive && 'cursor-pointer',
        priorityClasses[priority],
        className
      )}
      onClick={onClick}
      tabIndex={interactive ? 0 : undefined}
      onKeyPress={interactive ? (e) => e.key === 'Enter' && onClick?.() : undefined}
    >
      {/* Accent Bar with animation */}
      <div 
        className={cn(
          'accent-bar transition-all duration-200 group-hover:w-1.5',
          accent.bar
        )}
        aria-hidden="true"
      />
      
      <div className="pl-4 flex-1">
        {/* Header with title, icon, and info */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {title}
          </span>
          <div className="flex items-center gap-2">
            {icon && (
              <div className="text-gray-400 opacity-60">
                {icon}
              </div>
            )}
            {info && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors touch-target"
                      aria-label={`More information about ${title}`}
                    >
                      <Info size={14} className="text-gray-400" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent 
                    side="top" 
                    className="max-w-xs text-sm bg-gray-900 text-white p-3 rounded-lg shadow-lg"
                  >
                    {info}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        
        {/* Value Row */}
        <div className="flex items-baseline gap-3 mb-1">
          <div className="flex flex-col">
            <span 
              className="text-3xl font-bold text-gray-900 dark:text-white transition-transform duration-200 group-hover:scale-[1.02]"
              aria-live="polite"
            >
              {value}
            </span>
            {unit && (
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {unit}
              </span>
            )}
          </div>
          
          {/* Delta Badge */}
          {deltaInfo && (
            <div 
              className={cn(
                'flex items-center gap-0.5 text-sm font-medium animate-fade-in',
                deltaInfo.colorClass
              )}
              aria-label={`Change: ${deltaInfo.value}`}
            >
              <deltaInfo.icon size={14} aria-hidden="true" />
              <span>{deltaInfo.value}</span>
            </div>
          )}
        </div>
        
        {/* Subcopy */}
        {effectiveSubcopy && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {effectiveSubcopy}
          </p>
        )}
        
        {/* Sparkline */}
        {sparklineData && sparklineData.length > 0 && (
          <div className="mb-3 h-12 transition-opacity duration-200 group-hover:opacity-80">
            <Sparkline 
              data={sparklineData} 
              stroke={sparklineStroke}
              color={sparklineColor}
              height={48}
            />
          </div>
        )}
        
        {/* Footer */}
        {finalFooter && (
          <div className="pt-3 border-t border-gray-200/60 dark:border-gray-700/60">
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              {finalFooter}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KpiTile;
